/**
 * CommandCentre — the production tiles and live status light that used to sit
 * at the top of the standalone studio admin page.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Radio, Film, CalendarClock, ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

const Tile = ({ icon: Icon, label, value, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <p className="mt-3 text-3xl font-bold tabular-nums">{value ?? 0}</p>
    </CardContent>
  </Card>
);

export const CommandCentre = () => {
  const { data } = useQuery({
    queryKey: ['fwtv-studio-overview'],
    queryFn: () => api.get('/fwtv/studio/overview').then((r) => r.data),
    refetchInterval: 30000,
  });

  const status = data?.live_status || 'off_air';
  const statusStyle = status === 'live' ? 'bg-red-100 text-red-700'
    : status === 'standby' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600';

  return (
    <div className="space-y-5" data-testid="fwtv-command-centre">
      <div className="flex flex-wrap items-center gap-3">
        <Badge className={`${statusStyle} hover:${statusStyle}`} data-testid="fwtv-live-status">
          <Radio className="h-3 w-3 mr-1" />
          {status === 'live' ? 'On air' : status === 'standby' ? 'Standby' : 'Off air'}
        </Badge>
        {data?.next_live && (
          <span className="text-sm text-slate-600" data-testid="fwtv-next-live">
            Next: {data.next_live.title} · {new Date(data.next_live.start_at).toLocaleString()}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile icon={Film} label="Active programmes" value={data?.active_programmes} testId="tile-programmes" />
        <Tile icon={Film} label="Episodes in pipeline" value={data?.episodes_in_pipeline} testId="tile-pipeline" />
        <Tile icon={CalendarClock} label="Scheduled broadcasts" value={data?.scheduled_broadcasts} testId="tile-broadcasts" />
        <Tile icon={ShieldCheck} label="Awaiting approval" value={data?.awaiting_approval} testId="tile-approvals" />
      </div>

      <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
           data-testid="fwtv-production-rule">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <p>
          <strong>Production rule:</strong> no episode may be scheduled until media, thumbnail,
          metadata, captions and editorial approval are complete.
          {data?.media_incomplete ? ` ${data.media_incomplete} episode(s) still have outstanding checks.` : ''}
        </p>
      </div>
    </div>
  );
};

export default CommandCentre;
